/**
 * Device type detection utilities.
 */

import type { DeviceType } from "./types";
import { _detectOSInternal } from "./osDetection";
import { debugLogger } from "./debugLogger";

/**
 * Pure device detection logic without logging.
 * :param os: The detected operating system.
 * :return: The detected device type.
 */
function _detectDeviceTypeInternal(os: string): DeviceType {
  const userAgent = navigator.userAgent.toLowerCase();

  // Mobile devices
  if (os === "ios" || os === "android") {
    // Check for tablet indicators
    const isIPad = /ipad/.test(userAgent);
    const isAndroidTablet = os === "android" && !/mobile/.test(userAgent);
    const isLargeScreen = window.screen && window.screen.width >= 768;

    if (isIPad || isAndroidTablet || isLargeScreen) {
      return "tablet";
    }
    return "mobile";
  }

  // Desktop devices (Mac, Windows, Linux)
  if (os === "mac" || os === "windows" || os === "linux") {
    return "desktop";
  }

  return "unknown";
}

/**
 * Detects the device type from the user agent and screen properties.
 * :return: The detected device type.
 */
export function detectDeviceType(): DeviceType {
  // Use internal OS detection to avoid double logging when OS is already known
  const os = _detectOSInternal();
  const screenWidth = window.screen?.width;
  const screenHeight = window.screen?.height;

  debugLogger.log("Detecting device type...", {
    os,
    screenWidth,
    screenHeight,
  });

  const result = _detectDeviceTypeInternal(os);

  const userAgent = navigator.userAgent.toLowerCase();
  const details: Record<string, unknown> = { os, screenWidth };

  if (os === "ios" || os === "android") {
    const isIPad = /ipad/.test(userAgent);
    const isAndroidTablet = os === "android" && !/mobile/.test(userAgent);
    const isLargeScreen = window.screen && window.screen.width >= 768;

    if (result === "tablet") {
      details.isIPad = isIPad;
      details.isAndroidTablet = isAndroidTablet;
      details.isLargeScreen = isLargeScreen;
    }
  }

  debugLogger.logDetection("Device", result, details);

  return result;
}

/**
 * Detects if the current device is a desktop.
 * :return: True if running on a desktop device, false otherwise.
 */
export function isDesktop(): boolean {
  return detectDeviceType() === "desktop";
}

/**
 * Detects if the current device is a mobile device.
 * :return: True if running on a mobile device, false otherwise.
 */
export function isMobile(): boolean {
  return detectDeviceType() === "mobile";
}

/**
 * Detects if the current device is a tablet.
 * :return: True if running on a tablet device, false otherwise.
 */
export function isTablet(): boolean {
  return detectDeviceType() === "tablet";
}
